import { createContext } from 'react';
import { IPlannedStudies } from '../services/fhir/FHIRPatientResource';
import { IParticipant } from '../utils/appointmentUtils';
import { ICodeName, IDepartmentReasons, IDepartments } from './loginContext';

export interface ITimeslot {
    start: Date;
    end: Date;
    supressDuration?: boolean;
}

export interface IReferenceDisplay {
    reference: string;
    display: string;
}

export interface ITimeslotReplacedServiceType {
    coding: any;
}

export interface IAvailableTimeslot {
    timeslot: ITimeslot;
    replacedRoom: IReferenceDisplay;
    replacedPractitioner: IReferenceDisplay;
    replacedCareUnit: IReferenceDisplay;
    replacedDepartment: IReferenceDisplay;
    replacedServiceType: ITimeslotReplacedServiceType;
}

export interface IAvailableTimeslots {
    appointmentNr: number;
    availableTimeslot: IAvailableTimeslot[];
    supressDuration: boolean;
}

export interface ISelectedTimeslots {
    appointmentNr: number;
    selectedTimeslot: ITimeslot;
}

export interface ISelectedResources {
    appointmentNr: number;
    resource: any;
}

export interface IExtendedPhysicianAppointmentExtensionSub {
    url: string;
    valuePeriod?: ITimeslot;
    valueReference?: IReferenceDisplay;
    valueCodeableConcept?: ITimeslotReplacedServiceType;
    valueBoolean?: boolean;
}

export interface IExtendedPhysicianAppointmentExtension {
    url: string;
    valueString?: string;
    extension?: IExtendedPhysicianAppointmentExtensionSub[];
}

export interface IExtendedPhysicianAppointment {
    participant: IParticipant[];
    resourceType: string;
    start: Date;
    end: Date;
    status: string;
    serviceType: any[];
    identifier: [
        {
            system: string;
            value: string;
        },
    ];
    extension: IExtendedPhysicianAppointmentExtension[];
    modifierExtension: IExtendedPhysicianAppointmentExtensionSub[];
}

export interface ISelectedPhysician {
    lines?: string[];
    id: number;
    name: string;
    pict?: string;
    makroId?: string;
    appointments?: IExtendedPhysicianAppointment[];
    blockMessage?: string;
    hdBooking?: string;
    firstPossibleDate?: string;
}

export interface IPersonalData {
    insuranceNr: string;
    firstName: string;
    lastName: string;
    birthday: string;
    gender: string;
    email: string;
    telephone: string;
    street: string;
    housenr: string;
    city: string;
    zip: string;
    country: string;
    mobilePhone: string;
    academicTitle: string;
    eGK: string;
    kvBarcode: string;
    state: string;
    profilePhoto: string;
    plannedStudies: IPlannedStudies[];
    username: string;
    password: string;
    insuranceName: string;
    language: string;
}

export interface IPersonalDataContactPerson {
    firstName: string;
    lastName: string;
    email: string;
    telephone: string;
    street: string;
    housenr: string;
    city: string;
    zip: string;
    country: string;
}
export interface IHintsFragen {
    linkId: string;
    type: string;
    label: string;
    precision?: number;
    mandatory?: boolean;
}

export interface IHintsAnswers {
    linkId: string;
    valueString?: string;
    valueNumber?: string;
    valueBoolean?: boolean;
}

export interface IHints {
    department?: string;
    reason: string;
    aFilter?: string;
    pFilter?: string;
    Hinweis?: string;
    Fragen?: IHintsFragen[];
}

export interface IMakro {
    id?: number;
    code?: string;
    countAPKS?: number;
    name?: string;
    site?: string;
}
export interface IJourneyData {
    site?: string;
    hints?: IHints[];
    makros?: IMakro[];
}

export type InitialTerminierungType = {
    withLogin: boolean;
    withCheckIn: boolean;
    selectedCategory: {
        name: string;
        id: number;
        countAPKs: number;
        oneMacro: boolean;
        macroCode: string;
        macroType: string;
    };
    selectedLocation: ICodeName;
    selectedDepartment: IDepartments;
    hdBooking?: string;
    filteredReasons: IDepartmentReasons[];
    selectedReason: IDepartmentReasons;
    selectedReasonHints: IHintsAnswers[];
    selectedInsurance: string;
    selectedPhysician: ISelectedPhysician;
    selectedAppointmentType: string;
    availableTimeslots: IAvailableTimeslots[];
    // availableTimeslots_2: IAvailableTimeslot[];
    selectedTimeslots: ISelectedTimeslots[];
    timeslotClicked: boolean;
    // selectedTimeslot_2: ITimeslot;
    selectedResources: ISelectedResources[];
    // selectedResource_2: any;
    workOnAppointmentNr: number;
    personalData: IPersonalData;
    personalDataPhysPat: IPersonalData;
    personalDataContactPerson: IPersonalDataContactPerson;
    remark: string;
    earlierAppointmentWanted: boolean;
    enrollData: {
        ambId: string;
        enrollCode: string;
        maId: string;
        patNameFirst: string;
        patNameLast: string;
        scheduleTime: string;
        fhir: {
            participant: IParticipant[];
            resourceType: string;
            start: Date;
            end: Date;
            status: string;
            serviceType: any[];
        };
        waitNumber?: string;
        requestFromDirectLink: boolean | undefined;
        remark?: string;
        kvBarcode?: string;
        isToday?: boolean | undefined;
    };
    changePersonalData: boolean;
    canceledAppointment: boolean;
    cancelNewAppointmentData: {
        showModal: boolean;
        data: any;
        reason: string;
    };
    renewAppointment: boolean;
    journeyData: IJourneyData;
};

export const initialTerminierungState = {
    withLogin: false,
    withCheckIn: false,
    selectedCategory: {
        name: '',
        id: 0,
        countAPKs: 1,
        oneMacro: false,
        macroCode: '',
        macroType: '',
    },
    selectedLocation: {
        code: '',
        name: '',
    },
    selectedDepartment: {
        name: '',
        reasons: [],
    },
    hdBooking: '',
    filteredReasons: [],
    selectedReason: {
        makroCode: '',
        makroId: '',
        makroName: '',
        pFilter: '',
        aFilter: '',
        reasonCode: '',
        reasonId: '',
        reasonName: '',
        reasonMainCode: '',
    },
    selectedReasonHints: [],
    selectedPhysician: {
        lines: [],
        id: 0,
        name: '',
        pict: '',
        makroId: '',
    },
    selectedInsurance: '',
    selectedAppointmentType: '',
    availableTimeslots: [],
    // availableTimeslots_2: [],
    selectedTimeslots: [],
    timeslotClicked: false,
    // selectedTimeslot_2: {
    //     start: new Date(0),
    //     end: new Date(0),
    // },
    selectedResources: [],
    // selectedResource_2: '',
    workOnAppointmentNr: 1,
    personalData: {
        insuranceNr: '',
        firstName: '',
        lastName: '',
        birthday: '',
        gender: '',
        email: '',
        telephone: '',
        street: '',
        housenr: '',
        city: '',
        zip: '',
        country: '',
        mobilePhone: '',
        academicTitle: '',
        eGK: '',
        kvBarcode: '',
        state: '',
        profilePhoto: '',
        plannedStudies: [],
        username: '',
        password: '',
        insuranceName: '',
        language: '',
    },
    personalDataPhysPat: {
        insuranceNr: '',
        firstName: '',
        lastName: '',
        birthday: '',
        gender: '',
        email: '',
        telephone: '',
        street: '',
        housenr: '',
        city: '',
        zip: '',
        country: '',
        eGK: '',
        mobilePhone: '',
        academicTitle: '',
        kvBarcode: '',
        state: '',
        profilePhoto: '',
        plannedStudies: [],
        username: '',
        password: '',
        insuranceName: '',
        language: '',
    },
    personalDataContactPerson: {
        firstName: '',
        lastName: '',
        email: '',
        telephone: '',
        street: '',
        housenr: '',
        city: '',
        zip: '',
        country: '',
    },
    remark: '',
    earlierAppointmentWanted: false,
    enrollData: {
        ambId: '',
        enrollCode: '',
        maId: '',
        patNameFirst: '',
        patNameLast: '',
        scheduleTime: '',
        fhir: {
            participant: [],
            resourceType: '',
            start: new Date(0),
            end: new Date(0),
            status: '',
            serviceType: [],
        },
        waitNumber: '',
        requestFromDirectLink: undefined,
        remark: '',
        isToday: undefined,
    },
    changePersonalData: false,
    canceledAppointment: false,
    cancelNewAppointmentData: {
        showModal: false,
        data: '',
        reason: '',
    },
    renewAppointment: false,
    journeyData: {
        site: '',
        hints: [],
    },
};

const TerminierungContext = createContext<{
    tmstate: InitialTerminierungType;
    tmdispatch: React.Dispatch<any>;
}>({
    tmstate: initialTerminierungState,
    tmdispatch: () => null,
});

export default TerminierungContext;
